/* eslint-disable max-lines */
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { compact, flatMap, includes, isEmpty, isNil, map, values } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import lineItemEnum from 'enums/lineItem.enum';
import WorkQueues from 'enums/workQueue.enum';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { filterBulkLabels, listBulkLabelsByPage } from 'store/thunks/workQueueThunks';

import orderFillType from 'common/constants/orderFillType';
import { EnableInventoryLookup, casingFormat, formatStateFilterLabel } from 'common/utils';
import { BatchShipmentFilterType } from 'common/constants/notes';
import { tagsChipName } from 'modules/WorkQueue/utils/tagMatchMode';
import FacilityFilterStatusChip from './FacilityFilterStatusChip';

const BulkLabelsFilterStatusBar = ({ resetStaleLineItems }) => {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const filter = useSelector(({ workQueue }) => workQueue.bulkLabels?.filter);

  const formatTag = (tag) => casingFormat(tag?.replace(/_/g, ' '));
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const selectedDrugProfiles = useSelector(
    ({ workQueue }) => workQueue.bulkLabels?.filter?.selectedDrugProfiles || []
  );
  // POM-5850: catalog-backed Drug Name selections; each entry already carries its display label.
  const selectedDrugProducts = useSelector(
    ({ workQueue }) => workQueue.bulkLabels?.filter?.selectedDrugProducts || []
  );
  const practices =
    filter?.partnerIds && filter?.practiceIds
      ? compact(
          filter.partnerIds.flatMap((partnerId) => {
            const partner = partners?.find((p) => p.partnerUuid === partnerId);
            return (
              partner?.partnerPractices?.filter((practice) =>
                filter.practiceIds.includes(practice.practiceId)
              ) || []
            );
          })
        )
      : [];

  const partnerPractices = compact(flatMap(map(partners, 'partnerPractices')));

  const selectedPartnerPractices = partnerPractices
    .filter((pp) => includes(filter.practiceIds, pp.practiceId))
    .map((pp) => pp.practiceName);

  const practiceName = practices?.map((practice) =>
    practice.practiceName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
  );

  const isPracticeCleared = filter?.practiceIds && !practiceName;

  const getDateRange = (dateRange) => {
    const dateArray = Array.isArray(dateRange) ? dateRange : [dateRange];
    const dates = map(dateArray, (date) => {
      let dateObj;
      if (typeof date === 'string' && date.includes('T')) {
        dateObj = new Date(date);
      } else if (typeof date === 'string') {
        const [year, month, day] = date.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else {
        dateObj = date;
      }
      return format(dateObj, 'MM/dd/yyyy');
    });

    return dates.length === 1 ? `${dates[0]}` : `${dates[0]} - ${dates[1]}`;
  };

  useEffect(() => {
    if (isPracticeCleared) {
      dispatch(getPartnerPractices({ partnerIds: [filter.partnerIds] }));
    }
  }, [dispatch, isPracticeCleared, filter]);

  const removeFilter = (filterName) => () => {
    dispatch(
      filterBulkLabels({
        filter: {
          ...filter,
          [filterName]: undefined,
          ...(filterName === 'selectedDrugProfiles' && { selectedDrugProfiles: [] }),
          // Match selectedDrugProfiles: the filter panel reads these back on open, and `undefined`
          // would leave the last selection in place instead of clearing the chip.
          ...(filterName === 'selectedDrugProducts' && { selectedDrugProducts: [] }),
          ...(filterName === 'partnerIds' && { practiceIds: undefined }),
          ...(filterName === 'notInPartnerIds' && { practiceIds: undefined }),
          ...(filterName === 'createdAt' && { createdBetween: undefined }),
          ...(filterName === 'createdBetween' && { createdAt: undefined }),
          // POM-6330: a mode outliving its tags would widen the next selection (Urgent included).
          ...(filterName === 'tags' && { tagMatchMode: undefined }),
        },
      })
    );
  };

  const removeAllFilter = () => dispatch(filterBulkLabels({ filter: {} }));

  const handleRefresh = () => {
    setIsRefreshing(true);
    resetStaleLineItems();
    dispatch(
      workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: true })
    );
    dispatch(listBulkLabelsByPage({ page: 1 })).finally(() => {
      setIsRefreshing(false);
      dispatch(
        workQueueActions.setWorkQueueLoading({ preset: WorkQueues.BULK_LABELS, loading: false })
      );
    });
  };

  const notInPartnerNames = filter?.notInPartnerIds?.map((partnerId) => {
    const foundPartner = partners.find(
      (partner) => partner.partnerUuid.toLowerCase() === partnerId.toLowerCase()
    );
    const name = foundPartner?.partnerName || '';
    return casingFormat(name);
  });

  const notInPartnerLabel = formatStateFilterLabel(notInPartnerNames, 'Not In Partner');

  return (
    <Stack
      spacing={1}
      direction='row'
      flexWrap='wrap'
      rowGap={1}
      alignItems='center'
      sx={{ maxWidth: '80rem' }}
    >
      <FacilityFilterStatusChip
        removeFilter={removeFilter}
        selectedFacilities={filter?.fulfillingPharmacyIds || []}
      />

      {filter.itemType && (
        <Chip
          label={`Item Type = ${filter.itemType}`}
          variant='contained'
          onDelete={removeFilter('itemType')}
          size='small'
        />
      )}

      {filter.patientName && (
        <Chip
          label={`Name = ${filter.patientName}`}
          variant='contained'
          onDelete={removeFilter('patientName')}
          size='small'
        />
      )}

      {filter.medicationNames && (
        <Chip
          key='medication-names'
          label={
            <Tooltip title={filter.medicationNames.join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.medicationNames, 'Drug Name')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('medicationNames')}
          size='small'
        />
      )}

      {!isEmpty(selectedDrugProducts) && (
        <Chip
          key='drug-products'
          label={
            <Tooltip title={selectedDrugProducts.map((product) => product.label).join(', ')}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatStateFilterLabel(
                  selectedDrugProducts.map((product) => product.label),
                  'Drug Name'
                )}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('selectedDrugProducts')}
          size='small'
        />
      )}

      {!isEmpty(selectedDrugProfiles) && (
        <Chip
          key='drug-profiles'
          label={
            <Tooltip
              title={selectedDrugProfiles
                .map((drug) => {
                  return drug.strength && drug.form
                    ? `${drug.drugName} - ${drug.strength}`
                    : drug.drugName;
                })
                .join(', ')}
            >
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatStateFilterLabel(
                  selectedDrugProfiles.map((drug) => {
                    return drug.strength && drug.form
                      ? `${drug.drugName} - ${drug.strength}`
                      : drug.drugName;
                  }),
                  'Drug Profile'
                )}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('selectedDrugProfiles')}
          size='small'
        />
      )}

      {filter.tags && (
        <Chip
          key='tags'
          label={
            <Tooltip title={filter.tags.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tags.map(formatTag), tagsChipName(filter))}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tags')}
          size='small'
        />
      )}

      {filter.tagsNotIn && (
        <Chip
          key='tags-not-in'
          label={
            <Tooltip title={filter.tagsNotIn.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tagsNotIn.map(formatTag), 'Tags Not In')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tagsNotIn')}
          size='small'
        />
      )}

      {filter.partnerIds && (
        <Chip
          label={`Partner = ${filter.partnerIds
            .map((partnerId) => {
              const foundPartner = partners.find(
                (partner) => partner.partnerUuid.toLowerCase() === partnerId.toLowerCase()
              );
              const name = foundPartner?.partnerName || '';
              return casingFormat(name);
            })
            .join(', ')}`}
          key='partners'
          variant='contained'
          onDelete={removeFilter('partnerIds')}
          size='small'
        />
      )}

      {filter.notInPartnerIds && !isEmpty(selectedPartnerPractices) && (
        <Chip
          label={
            <Tooltip title={`Practice = ${selectedPartnerPractices.join(',')}`}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(selectedPartnerPractices, 'Practice')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('practiceIds')}
          size='small'
        />
      )}

      {filter.partnerIds && filter.practiceIds && (
        <Chip
          label={
            <Tooltip title={practiceName.join(',')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(practiceName, 'Practice')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('practiceIds')}
          size='small'
        />
      )}

      {filter.notInPartnerIds && (
        <Chip
          label={
            <Tooltip
              title={filter.notInPartnerIds
                .map((partnerId) => {
                  const foundPartner = partners.find(
                    (partner) => partner.partnerUuid.toLowerCase() === partnerId.toLowerCase()
                  );
                  const name = foundPartner?.partnerName || '';
                  return casingFormat(name);
                })
                .join(', ')}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {notInPartnerLabel}
              </Box>
            </Tooltip>
          }
          key='notInPartnerIds'
          variant='contained'
          onDelete={removeFilter('notInPartnerIds')}
          size='small'
        />
      )}

      {filter?.states && (
        <Chip
          label={
            <Tooltip title={filter?.states?.join(',')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter?.states, 'States')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('states')}
          size='small'
        />
      )}

      {filter.fillType && (
        <Chip
          label={`Fill Type = ${orderFillType[filter.fillType]}`}
          variant='contained'
          onDelete={removeFilter('fillType')}
          size='small'
        />
      )}

      {filter.shipByDate && (
        <Chip
          label={`Ship By Date = ${format(parseISO(filter.shipByDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter('shipByDate')}
          size='small'
        />
      )}

      {EnableInventoryLookup && !isEmpty(filter.ndcs) && (
        <Chip
          key='ndcs'
          label={
            <Tooltip title={filter.ndcs.join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.ndcs, 'NDC')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('ndcs')}
          size='small'
        />
      )}

      {filter.orderId && (
        <Chip
          label={`Order Id = ${filter.orderId}`}
          variant='contained'
          onDelete={removeFilter('orderId')}
          size='small'
        />
      )}

      {filter.orderDate && (
        <Chip
          label={`Order Date = ${format(new Date(filter.orderDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter('orderDate')}
          size='small'
        />
      )}

      {filter.labelDate && (
        <Chip
          label={`Label Date = ${format(new Date(filter.labelDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter('labelDate')}
          size='small'
        />
      )}

      {filter.lastPrintedDate && (
        <Chip
          label={`Last Printed Date = ${format(new Date(filter.lastPrintedDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter('lastPrintedDate')}
          size='small'
        />
      )}

      {filter?.createdAt && (
        <Chip
          label={`Date = ${getDateRange(filter.createdAt)}`}
          variant='contained'
          onDelete={removeFilter('createdAt')}
        />
      )}

      {filter?.createdBetween && (
        <Chip
          label={`Date Range = ${getDateRange(filter.createdBetween)}`}
          variant='contained'
          onDelete={removeFilter('createdBetween')}
        />
      )}

      {!isNil(filter.isPrinted) && (
        <Chip
          label={`Printed = ${filter.isPrinted ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('isPrinted')}
          size='small'
        />
      )}

      {!isNil(filter.taggedForReview) && (
        <Chip
          label={`Tagged For Review = ${filter.taggedForReview ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('taggedForReview')}
          size='small'
        />
      )}

      {!isNil(filter.lineItemStatus) && (
        <Chip
          label={`Filled = ${
            filter.lineItemStatus === lineItemEnum.PRODUCT_FULFILLMENT ? 'No' : 'Yes'
          }`}
          variant='contained'
          onDelete={removeFilter('lineItemStatus')}
          size='small'
        />
      )}
      {!isNil(filter.isReshipment) && (
        <Chip
          label={`Reshipments = ${filter.isReshipment ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('isReshipment')}
          size='small'
        />
      )}

      {filter.batchStatus && (
        <Chip
          label={`Batch Items = ${BatchShipmentFilterType[filter.batchStatus]}`}
          variant='contained'
          onDelete={removeFilter('batchStatus')}
          size='small'
        />
      )}

      {filter.itemCount && (
        <Chip
          label={`Item Count = ${filter.itemCount}`}
          variant='contained'
          onDelete={removeFilter('itemCount')}
          size='small'
        />
      )}

      {filter.quantity && (
        <Chip
          label={`Qty = ${filter.quantity}`}
          variant='contained'
          onDelete={removeFilter('quantity')}
          size='small'
        />
      )}

      {(!isEmpty(compact(values(filter))) || !isNil(filter.isPrinted)) && (
        <Button onClick={removeAllFilter}>Remove All</Button>
      )}

      <Tooltip title='Refresh list' arrow placement='top'>
        <div>
          <IconButton onClick={handleRefresh} size='small' color='primary' disabled={isRefreshing}>
            {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </div>
      </Tooltip>
    </Stack>
  );
};

BulkLabelsFilterStatusBar.propTypes = {
  resetStaleLineItems: PropTypes.func.isRequired,
};

export default BulkLabelsFilterStatusBar;
